import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NbButtonModule, NbCardModule, NbInputModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RobotPartHealth } from 'src/app/models/qa-models/robot-part-health';
import { Robot } from 'src/app/models/robot';
import { SharedModule } from 'src/app/shared/shared.module';
import { LoadAllRobots, LoadOwnerRobots } from '../redux';
import { QaState } from '../redux/qa.state';
import { QaServiceService } from '../services/qa-service.service';
import { RobotPartHealthService } from '../services/robot-part-health.service';

@Component({
  selector: 'app-robot-part-health',
  templateUrl: './robot-part-health.component.html',
  styleUrl: './robot-part-health.component.scss',
  standalone: true,
  imports: [SharedModule, NbCardModule, NbButtonModule, NbInputModule, SharedModule]
})
export class RobotPartHealthComponent implements OnInit, OnDestroy {
  public robot$: Observable<any[]> = inject(Store).select(QaState.robots);
  public ownerRobot$: Observable<any[]> = inject(Store).select(QaState.ownerRobots);
  private unsubscribe = new Subject<void>();

  public myRobots: Robot[] = [];
  public commonRobots: Robot[] = [];
  public partHealth: RobotPartHealth = null;
  public partHealthForm: UntypedFormGroup;
  public selectedRobot: Robot = null;
  public submitting = false;
  public spin = false;

  constructor(
    private store: Store,
    public service: QaServiceService,
    private robotPartHealthService: RobotPartHealthService
  ) {
    if (this.store.selectSnapshot(QaState.robots).length < 1) {
      this.store.dispatch(new LoadAllRobots());
    }
    if (!this.store.selectSnapshot(QaState.ownerRobots)) {
      this.store.dispatch(new LoadOwnerRobots());
    }
  }

  public ngOnInit(): void {
    this.robot$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.commonRobots = data));
    this.ownerRobot$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.myRobots = data));
  }

  private clonePartHealth(source: RobotPartHealth): RobotPartHealth {
    const copy = Object.assign(new RobotPartHealth(), { ...source });
    copy.part_counters = source.part_counters.map((counter: any) => {
      const total = counter.count_used + counter.count_remaining;
      const percentage = Math.round((counter.count_used / total) * 100);
      return { ...counter, count_total: total, counter: percentage };
    });
    return copy;
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public groupByFn = (item: any) => {
    return this.myRobots?.some((s: any) => s.id === item.id) ? 'My Robots' : 'Common Robots';
  };

  public robotIdAdd(event: Robot) {
    this.partHealth = null;
    this.selectedRobot = event;
    this.spin = this.selectedRobot !== null;
    if (event?.id) {
      this.robotPartHealthService.loadRobotPartHealth(event.id).then((data) => {
        this.spin = false;
        if (data) {
          this.partHealth = this.clonePartHealth(data);
        }
      });
    }
  }

  public robotIdClear() {
    this.partHealth = null;
    this.selectedRobot = null;
    this.spin = false;
  }

  public formatLabel(value: number): string {
    return `${Math.round(value)}%`;
  }

  public onSliderChange(event: Event, i: number) {
    const part = this.partHealth.part_counters[i];
    const input = event.target as HTMLInputElement;
    const percentage = Number(input.value) / 100;
    part.count_used = Math.round(part.count_total * percentage);
    part.count_remaining = Math.round(part.count_total - part.count_used);
    if (part.minutes_used >= 0) {
      part.minutes_used = Math.round(part.count_total * percentage);
      part.minutes_remaining = Math.round(part.count_total - part.minutes_used);
    }
  }

  public onSubmit() {
    this.submitting = true;
    this.robotPartHealthService
      .updateRobotPartHealth(this.selectedRobot.id, this.partHealth)
      .then((updated) => {
        if (updated) {
          this.partHealth = this.clonePartHealth(updated);
        }
      })
      .finally(() => (this.submitting = false));
  }
}
